// src/pages/Integrations/ApiKeyModal.tsx
import React, { useState, useEffect } from "react";
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Form,
  FormGroup,
  Label,
  Input,
  FormText,
} from "reactstrap";

interface CredentialField {
  field: string;
  label: string;
  type: "text" | "password";
  placeholder?: string;
  helper?: string;
}

interface Props {
  show: boolean;
  appName: string;
  installName?: string;
  credentials: CredentialField[];
  onClose: () => void;
  onSubmit: (config: Record<string, string>) => void;
}

const ApiKeyModal: React.FC<Props> = ({
  show,
  appName,
  installName,
  credentials,
  onClose,
  onSubmit,
}) => {
  // keep track of the editable connection name
  const [connectionName, setConnectionName] = useState(installName || "");
  // credential values keyed by `field`
  const [values, setValues] = useState<Record<string, string>>({});

  // initialize credential state whenever `credentials` array changes
  useEffect(() => {
    setValues(
      credentials.reduce((acc, c) => ({ ...acc, [c.field]: "" }), {})
    );
  }, [credentials]);

  // check that name + all credential fields are filled
  const allFilled =
    connectionName.trim().length > 0 &&
    credentials.every((c) => values[c.field]?.trim() !== "");

  const handleChange = (field: string, val: string) => {
    setValues((v) => ({ ...v, [field]: val }));
  };

  const submit = () => {
    onSubmit({ name: connectionName, ...values });
  };

  return (
    <Modal isOpen={show} centered backdrop="static">
      <ModalHeader toggle={onClose}>
        {installName
          ? `Configure ${installName}`
          : `New ${appName} Connection`}
      </ModalHeader>
      <ModalBody>
        <Form onSubmit={(e) => { e.preventDefault(); submit(); }}>
          <FormGroup>
            <Label for="connectionName">Connection Name</Label>
            <Input
              id="connectionName"
              type="text"
              value={connectionName}
              onChange={(e) => setConnectionName(e.target.value)}
              placeholder={`e.g. “My ${appName} account”`}
              required
            />
            <FormText color="muted">
              Give this integration instance a memorable name.
            </FormText>
          </FormGroup>

          {credentials.map(({ field, label, type, placeholder, helper }) => (
            <FormGroup key={field}>
              <Label for={field}>{label}</Label>
              <Input
                id={field}
                type={type}
                placeholder={placeholder}
                value={values[field] || ""}
                onChange={(e) => handleChange(field, e.target.value)}
                required
              />
              {helper && <FormText color="muted">{helper}</FormText>}
            </FormGroup>
          ))}
        </Form>
      </ModalBody>
      <ModalFooter>
        <Button outline onClick={onClose}>Cancel</Button>
        <Button color="primary" disabled={!allFilled} onClick={submit}>
          Save
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default ApiKeyModal;
